import React, { useRef } from 'react'
import PropTypes from 'prop-types'
import PremiumButton from '../PremiumButton/PremiumButton'
import Header from '../Header/Header'
import { reviewCount, googleRating, appleRating } from 'lifesum-shared/reviews'
import { FormattedMessage } from 'react-intl'
import messages from './messages'
import { trackReadMoreInHero } from '../../analytics'
import IntroVideo from './IntroVideo/IntroVideo'
import { android } from '../../device'
import AppleHealthBadge from './Apple_Health_badge_US-UK_blk_sRGB.svg'
import AppleHealthBadgeFR from './Apple_Health_badge_FR_blk_sRGB.svg'
import LogoApple from '!!@svgr/webpack!../../../assets/media/logo-apple.svg'
import LogoAndroid from '!!@svgr/webpack!../../../assets/media/logo-android.svg'
import AppleHealthBadgeDimensions from 'image-dimensions-loader!./Apple_Health_badge_US-UK_blk_sRGB.svg'
import { isSSR } from '../../isSSR'
import LazyImage from 'js/components/LazyImage/LazyImage'
import useLocale from '../../hooks/useLocale'
import Mask from '../Mask'
import StarRating from '../StarRating/StarRating'

const ReviewBlock = ({ type }) => {
  const Logo = type === 'google' ? LogoAndroid : LogoApple
  return (
    <li>
      <span className="sr-only">
        {type === 'google' ? 'Google Play: ' : 'Apple AppStore: '}
      </span>
      <Logo className="mt-20 -ml-2 mb-4" style={{ width: 34, height: 34 }} />
      <div className="flex">
        <div className="mr-4 xl:mr-6">
          <StarRating rating={type === 'google' ? googleRating : appleRating} />
        </div>
        <span className="lg:font-demibold xl:text-32 xl:font-regular hidden sm:block mt-2 xl:-mt-8">
          {type === 'google' ? googleRating : appleRating}
        </span>
      </div>
      <span className="sr-only">average rating</span>
    </li>
  )
}

ReviewBlock.propTypes = {
  type: PropTypes.oneOf(['google', 'apple']).isRequired,
}

const WorksWith = ({ language }) => {
  const { width, height } = AppleHealthBadgeDimensions

  // To prevent layout shifting on mobile ios browsers we always preserve
  // space for the apple health svg (accepting the side effect that we get
  // "unnecessary" whitespace on android)
  let src = false
  if (!isSSR && !android) {
    src = language === 'fr' ? AppleHealthBadgeFR : AppleHealthBadge
  }

  return (
    <LazyImage
      className={android ? 'invisible' : ''}
      src={src}
      alt="Works with Apple Health"
      width={width}
      height={height}
    />
  )
}

WorksWith.propTypes = {
  language: PropTypes.string,
}

function Hero({ premium, onClickFunc, ...rest }) {
  const wrapper = useRef()
  const bg = useRef()
  const video = useRef()
  const { language } = useLocale()

  const scrollToContent = () => {
    trackReadMoreInHero()

    const destination =
      video.current.offsetParent !== null
        ? bg.current.getBoundingClientRect().height - 100
        : wrapper.current.getBoundingClientRect().height

    window.scrollTo({ top: destination, behavior: 'smooth' })
  }

  return (
    <div ref={wrapper}>
      <Mask>
        <div className="bg-BgSub">
          <Header sticky={false} inverted={false} />
          <div
            className="bg-BgSub relative pt-24 md:pt-48 pb-72 lg:pb-128 text-center lg:text-left"
            ref={bg}
          >
            <div className="o-width-limiter">
              <div className="flex justify-between flex-col lg:flex-row">
                <div className="relative z-10 lg:w-2/5 my-auto">
                  <h1
                    className="type-large-fat xl:type-56-bold text-green"
                    data-uid="intro-title"
                  >
                    <FormattedMessage id="Jumbotron.intro.title" />
                    {language === 'fr' ||
                    language === 'ru' ||
                    language === 'it' ||
                    language === 'pt' ? (
                      ' '
                    ) : (
                      <br />
                    )}
                    <FormattedMessage {...messages.subTitle} />
                  </h1>
                  <p className="type-20 md:type-24 lg:type-28 xl:type-36 mt-16 lg:mt-24 mb-24 lg:mb-40">
                    <FormattedMessage {...messages.homepage_tagline} />
                  </p>
                  <div className="lg:hidden" ref={video}>
                    <IntroVideo mobile {...rest} />
                  </div>
                  <div className="lg:flex flex-wrap justify-start md:max-w-480 mx-auto lg:max-w-none xl:-mr-48">
                    <div className="xl:pr-16 md:w-full xl:w-1/2 xl:flex">
                      <button
                        onClick={scrollToContent}
                        className={`
                          c-button c-premium-button__cta
                          border-2 border-grey-darkest hover:text-green hover:border-green bg-transparent shadow-none
                          mb-12 xl:mb-0 mt-0 min-w-0 md:w-full
                        `}
                      >
                        <span className="truncate">
                          <FormattedMessage {...messages.readMore} />
                        </span>
                      </button>
                    </div>
                    {!premium && (
                      <>
                        <div className="hidden lg:block flex-grow xl:w-1/2">
                          <PremiumButton
                            signUpCopyBlock={true}
                            marginless={true}
                            signupCopyShadowBlack={true}
                            onClickFunc={onClickFunc}
                            newStartpage={true}
                            priceInside={true}
                            GALabel="Desktop hero CTA"
                          />
                        </div>
                        <div className="lg:hidden">
                          <PremiumButton
                            signUpCopyBlock={true}
                            marginless={true}
                            signupCopyShadowBlack={true}
                            onClickFunc={onClickFunc}
                            newStartpage={true}
                            GALabel="Mobile hero CTA"
                          />
                        </div>
                      </>
                    )}
                  </div>
                </div>
                <div
                  className="hidden lg:block"
                  style={{ width: '30%', minWidth: '380px' }}
                >
                  <IntroVideo {...rest} />
                </div>
                <div className="lg:w-1/5 mt-24 lg:my-auto">
                  <p className="lg:text-28 xl:text-32">
                    <strong className="font-bold">
                      {new Intl.NumberFormat(language).format(reviewCount)}
                    </strong>{' '}
                    <FormattedMessage {...messages.homepage_reviews_copy} />
                  </p>
                  <ul className="flex lg:block justify-evenly">
                    <ReviewBlock type="apple" />
                    <ReviewBlock type="google" />
                  </ul>
                  <div className="mt-24 lg:mt-48">
                    <WorksWith language={language} />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Mask>
    </div>
  )
}

Hero.propTypes = {
  premium: PropTypes.bool.isRequired,
  onClickFunc: PropTypes.func,
}

export default Hero
